import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Eraser, Maximize2, Check, X } from "lucide-react";

// Pointer-event based pad: works with mouse, touch AND graphic pen tablets
// (e.g. XP-Pen StarG430S). Graphic tablets map their whole surface to the full
// screen (absolute mode), so signing in a tiny box is impossible — the cursor
// jumps across the screen. The "Full screen" mode opens a large canvas that
// fills the viewport so the pen maps ~1:1 and signing feels natural.
export const SignaturePad = forwardRef(({ label, testid, onChange }, ref) => {
  const canvasRef = useRef(null);       // small inline canvas (final storage, 500x150)
  const fsCanvasRef = useRef(null);      // big full-screen canvas
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);
  const [fs, setFs] = useState(false);

  const prep = (canvas) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#15423A";
    return ctx;
  };

  useEffect(() => { prep(canvasRef.current); }, []);

  // When full-screen opens, size the big canvas to the overlay and copy any
  // existing signature into it so the user can continue from where they were.
  useEffect(() => {
    if (!fs) return;
    const fsc = fsCanvasRef.current;
    if (!fsc) return;
    const r = fsc.getBoundingClientRect();
    fsc.width = Math.round(r.width);
    fsc.height = Math.round(r.height);
    prep(fsc);
    if (!empty) {
      fsc.getContext("2d").drawImage(canvasRef.current, 0, 0, fsc.width, fsc.height);
    }
  }, [fs]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPos = (e) => {
    const c = e.currentTarget;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  };

  // Map pen pressure (0..1) to a line width. Mouse reports 0 → use a steady width.
  const widthFor = (e, scale = 1) => {
    const p = e.pressure && e.pressure > 0 && e.pointerType === "pen" ? e.pressure : 0.5;
    return (0.8 + p * 3.2) * scale;
  };

  const start = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { console.warn(err); }
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = e.currentTarget;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    const pos = getPos(e);
    ctx.lineWidth = widthFor(e, c === fsCanvasRef.current ? 2.2 : 1);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (c !== fsCanvasRef.current) setEmpty(false);
  };

  const end = (e) => {
    const was = drawing.current;
    drawing.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { console.warn(err); }
    if (was && onChange && e.currentTarget === canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clearCanvas = (c) => {
    c.getContext("2d", { willReadFrequently: true }).clearRect(0, 0, c.width, c.height);
  };

  const clear = () => {
    clearCanvas(canvasRef.current);
    setEmpty(true);
    onChange?.("");
  };

  // Full-screen "Done" → scale the big drawing down into the storage canvas.
  const applyFullScreen = () => {
    const base = canvasRef.current;
    const ctx = prep(base);
    ctx.clearRect(0, 0, base.width, base.height);
    ctx.drawImage(fsCanvasRef.current, 0, 0, base.width, base.height);
    setEmpty(false);
    setFs(false);
    onChange?.(base.toDataURL("image/png"));
  };

  useImperativeHandle(ref, () => ({
    getData: () => (empty ? "" : canvasRef.current.toDataURL("image/png")),
    isEmpty: () => empty,
    clear,
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-3">
          <button type="button" data-testid={testid ? `${testid}-fullscreen` : undefined} onClick={() => setFs(true)}
            className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium">
            <Maximize2 size={12} /> Full screen
          </button>
          <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
            <Eraser size={12} /> Clear
          </button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        data-testid={testid}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        className="w-full h-[110px] rounded-md border border-input bg-white touch-none cursor-crosshair"
        style={{ touchAction: "none" }}
      />
      <p className="text-[11px] text-muted-foreground mt-1">Mouse or touch works here. Using an XP-Pen tablet? Tap <b>Full screen</b> for an accurate signing area.</p>

      {fs && createPortal(
        <div className="fixed inset-0 z-[100] bg-foreground/60 backdrop-blur-sm flex flex-col p-4 sm:p-6 overflow-hidden" style={{ pointerEvents: "auto" }} data-testid={testid ? `${testid}-fs-overlay` : undefined}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-white">
              <div className="font-display text-xl">{label || "Signature"}</div>
              <div className="text-xs text-white/70">Sign anywhere in the box below — your pen tablet maps to this full area.</div>
            </div>
            <button type="button" onClick={() => setFs(false)} data-testid={testid ? `${testid}-fs-close` : undefined}
              className="h-9 w-9 rounded-md bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors">
              <X size={18} />
            </button>
          </div>
          <canvas
            ref={fsCanvasRef}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            onPointerCancel={end}
            data-testid={testid ? `${testid}-fs-canvas` : undefined}
            className="flex-1 min-h-0 w-full rounded-lg border-2 border-white bg-white touch-none cursor-crosshair"
            style={{ touchAction: "none" }}
          />
          <div className="flex items-center justify-end gap-3 mt-3">
            <button type="button" onClick={() => clearCanvas(fsCanvasRef.current)} data-testid={testid ? `${testid}-fs-clear` : undefined}
              className="h-11 px-5 rounded-md bg-white/15 text-white font-medium flex items-center gap-2 hover:bg-white/25 transition-colors">
              <Eraser size={16} /> Clear
            </button>
            <button type="button" onClick={applyFullScreen} data-testid={testid ? `${testid}-fs-done` : undefined}
              className="h-11 px-6 rounded-md bg-accent text-accent-foreground font-medium flex items-center gap-2 hover:bg-accent/90 transition-colors">
              <Check size={16} /> Done
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});
